import { useEffect, useRef, useState } from 'react'
import { env } from '@/lib/env'

/**
 * Single shared WebSocket connection used by alerts (AF-126),
 * notifications (AF-134), and any other realtime feature.
 *
 * Strategy:
 *   - Lazy connect on first subscriber, share across consumers via a singleton.
 *   - Auto-reconnect with exponential backoff (1s, 2s, 4s, …, 30s cap).
 *   - Pause reconnects while the document is hidden.
 *
 * The hook is intentionally typed as `unknown` payloads — each feature's
 * subscriber should validate the parsed JSON with zod.
 */

type Listener = (event: WsEvent) => void

export interface WsEvent {
  type: string
  payload: unknown
}

interface SharedSocket {
  refs: number
  socket: WebSocket | null
  reconnectMs: number
  reconnectTimer: number | null
  listeners: Set<Listener>
  statusListeners: Set<() => void>
  intentionalClose: boolean
}

const shared: SharedSocket = {
  refs: 0,
  socket: null,
  reconnectMs: 1000,
  reconnectTimer: null,
  listeners: new Set(),
  statusListeners: new Set(),
  intentionalClose: false,
}

function notifyStatus(): void {
  shared.statusListeners.forEach((l) => l())
}

function connect(): void {
  if (shared.socket && shared.socket.readyState <= WebSocket.OPEN) return
  // MSW only intercepts HTTP; a `new WebSocket('/ws')` here bypasses
  // the worker and hits Vite's dev proxy, which can't reach the
  // backend in mocked / CI runs and floods stdout with ECONNREFUSED.
  // The audit + smoke specs don't exercise realtime — skip the
  // connection entirely when running with mocks OR under vitest.
  // The vitest gate matters because the test suite mounts pages that
  // mount useWsSubscription without seeding VITE_USE_MOCKS=true; the
  // bare jsdom WebSocket would otherwise trigger MSW's
  // "intercepted a WebSocket connection without a matching event
  // handler" warning.
  if (env.VITE_USE_MOCKS) return
  if (import.meta.env.MODE === 'test') return
  const url = resolveUrl(env.VITE_WS_URL)
  const ws = new WebSocket(url)
  shared.socket = ws
  shared.intentionalClose = false

  ws.addEventListener('open', () => {
    shared.reconnectMs = 1000
    notifyStatus()
  })

  ws.addEventListener('message', (msg) => {
    let parsed: WsEvent | null = null
    try {
      const data = JSON.parse(msg.data as string) as unknown
      if (typeof data === 'object' && data !== null && 'type' in data) {
        parsed = data as WsEvent
      }
    } catch {
      parsed = null
    }
    if (parsed) {
      shared.listeners.forEach((l) => l(parsed))
    }
  })

  ws.addEventListener('close', () => {
    shared.socket = null
    notifyStatus()
    if (shared.intentionalClose) return
    if (document.visibilityState === 'hidden') return
    if (shared.refs === 0) return
    const delay = shared.reconnectMs
    shared.reconnectMs = Math.min(shared.reconnectMs * 2, 30_000)
    shared.reconnectTimer = window.setTimeout(connect, delay)
  })
}

function resolveUrl(path: string): string {
  if (/^wss?:\/\//.test(path)) return path
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}${path}`
}

function subscribe(listener: Listener): () => void {
  shared.listeners.add(listener)
  shared.refs += 1
  if (!shared.socket) connect()

  return () => {
    shared.listeners.delete(listener)
    shared.refs -= 1
    if (shared.refs === 0) {
      shared.intentionalClose = true
      if (shared.reconnectTimer) {
        window.clearTimeout(shared.reconnectTimer)
        shared.reconnectTimer = null
      }
      shared.socket?.close()
      shared.socket = null
    }
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && shared.refs > 0 && !shared.socket) {
      connect()
    }
  })
}

/**
 * Subscribe to a specific event type. Filtering happens here so each consumer
 * doesn't need to switch on `type` themselves.
 *
 * Uses the "latest ref" pattern: the subscription only re-binds when `type`
 * changes, but always calls the freshest handler. Avoids re-subscribing on
 * every render just because a closure changed.
 *
 * @example
 * useWsSubscription('alerts.created', (e) => queryClient.invalidateQueries(...))
 */
export function useWsSubscription(type: string | RegExp, handler: (event: WsEvent) => void): void {
  const handlerRef = useRef(handler)

  // Update the ref *after* commit so we never write to it during render.
  useEffect(() => {
    handlerRef.current = handler
  })

  useEffect(() => {
    const matches = (t: string) => (typeof type === 'string' ? t === type : type.test(t))
    return subscribe((event) => {
      if (matches(event.type)) handlerRef.current(event)
    })
  }, [type])
}

function readStatus(): 'connecting' | 'open' | 'closed' {
  const s = shared.socket
  if (!s) return 'closed'
  if (s.readyState === WebSocket.OPEN) return 'open'
  if (s.readyState === WebSocket.CONNECTING) return 'connecting'
  return 'closed'
}

/** Track WS connection state for status indicators. */
export function useWsStatus(): 'connecting' | 'open' | 'closed' {
  const [status, setStatus] = useState<'connecting' | 'open' | 'closed'>(readStatus)

  useEffect(() => {
    const update = () => setStatus(readStatus())
    shared.statusListeners.add(update)
    shared.refs += 1
    if (!shared.socket) connect()
    // Reflect the post-connect state immediately — connect() flips
    // shared.socket synchronously, so readyState is now CONNECTING.
    update()

    return () => {
      shared.statusListeners.delete(update)
      shared.refs -= 1
      if (shared.refs === 0) {
        shared.intentionalClose = true
        if (shared.reconnectTimer) {
          window.clearTimeout(shared.reconnectTimer)
          shared.reconnectTimer = null
        }
        shared.socket?.close()
        shared.socket = null
      }
    }
  }, [])

  return status
}
